import { getDeps, getHandler, type OnEscKeydownHandler, useConfirmationDialog } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';

type UseConfirmCloseProps = {
  isDirty: boolean;
  isMutationPending?: boolean;
  onCloseParent: OnEscKeydownHandler;
  onClearDirty?: () => void;
  title?: string;
  description?: string;
};

const useConfirmClose = ({
  isDirty,
  isMutationPending,
  onCloseParent,
  onClearDirty,
  title,
  description,
}: UseConfirmCloseProps) => {
  const { t } = useTranslation('playbook-builder', { keyPrefix: 'confirmClose' });
  const confirm = useConfirmationDialog();
  const onCloseParentHandler = getHandler(onCloseParent);

  const action = () => {
    onClearDirty?.();

    // required due to the animation
    setTimeout(() => {
      onCloseParentHandler();
    }, 200);
  };

  const attemptClose: OnEscKeydownHandler = {
    handler: () => {
      if (isMutationPending) return;

      if (!isDirty) {
        return action();
      }

      confirm.open({
        title: title || t($ => $.title),
        description: description || t($ => $.description),
        variant: 'destructive',
        primaryButton: {
          label: t($ => $.confirm),
          onClick: action,
        },
        secondaryButton: {
          label: t($ => $.cancel),
        },
      });
    },
    deps: [isMutationPending, isDirty, ...getDeps(onCloseParent)],
  };

  return { attemptClose };
};

export default useConfirmClose;
